package dev.lingvo.shared

import kotlinx.serialization.Serializable

/**
 * Where a translation sits in review.
 *
 * **A separate column, not a new [TranslationState] value.** `TranslationState` is a wire contract
 * shared with the CLI, with every stored export and with `Progress`'s `translated >= approved`
 * invariant; adding `IN_REVIEW` to it would break every deployed CLI and every export on disk.
 */
@Serializable
enum class ReviewStatus {
    /** Nobody is waiting on anybody. Both a fresh string and an approved one. */
    NONE,

    /** Typed and submitted. This is what the review queue reads. */
    PENDING,

    /** A reviewer asked for something, and said what. Back with the translator. */
    CHANGES_REQUESTED,
}

/** What a reviewer does with one string. */
@Serializable
enum class ReviewAction { APPROVE, REQUEST_CHANGES, SKIP }

/**
 * One string, with everything needed to decide about it without leaving the page.
 *
 * A queue, not a grid: the whole point is that a reviewer sees one thing at a time, at full width,
 * with the source, the submission, what changed, who wrote it and what QA thinks — and never has to
 * open another tab to answer "is this right".
 */
@Serializable
data class ReviewItemDto(
    val keyId: String,
    val keyName: String,
    val context: String? = null,
    val sourceValue: String? = null,
    val sourcePluralForms: Map<PluralCategory, String>? = null,
    val targetValue: String? = null,
    val targetPluralForms: Map<PluralCategory, String>? = null,
    /** The last approved text, for the word diff. Null when this is the first submission. */
    val previousApproved: String? = null,
    val submittedBy: String? = null,
    val submittedAt: String? = null,
    val origin: TranslationOrigin = TranslationOrigin.HUMAN,
    val issues: List<QaIssueDto> = emptyList(),
    val comments: List<CommentDto> = emptyList(),
    /**
     * The version this reviewer is deciding about.
     *
     * Sent back with the decision, so a string somebody else reviewed while this one was being read
     * is refused rather than double-decided.
     */
    val updatedAt: String? = null,
    val sourceSnapshot: SourceSnapshotDto? = null,
    val targetRevision: String? = null,
) {
    /** Whether approving it would be refused by the QA gate. Drawn on the button, not discovered. */
    val blocked: Boolean get() = issues.any { it.severity == QaSeverity.ERROR }
}

/**
 * The queue for one locale.
 *
 * [approvedLastWeek] exists for the empty state: an empty review queue is good news and should read
 * like it, rather than like a page that failed to load.
 */
@Serializable
data class ReviewQueueDto(
    val locale: String,
    /** The BCP-47 locale of every [ReviewItemDto.sourceValue] and source plural form. */
    val sourceLocale: String? = null,
    val total: Int,
    val items: List<ReviewItemDto> = emptyList(),
    val approvedLastWeek: Int = 0,
    /** Whether this person may decide here at all. False means the page offers reading, not buttons. */
    val canApprove: Boolean = false,
    /**
     * How many of the pending strings the approval gate would let through right now.
     *
     * Counted on the server by running the same QA evaluation the gate runs, at this project's
     * severities — not inferred from [items], which is one screenful. It is what the header sizes
     * the batch action from, so the number on the button is the number the server will accept.
     *
     * Bounded: at most [MAX_REVIEW_BATCH] pending strings are evaluated, because that is the most
     * one batch may carry anyway. When [total] exceeds that, this is a floor rather than a census.
     */
    val qaCleanPending: Int = 0,
    /**
     * Pass back as `after` for the next screenful, or null when this was the last.
     *
     * Opaque, and a keyset rather than an offset for the reason every other list here is: reviewers
     * decide strings while they page, which is exactly the write that makes an offset skip rows.
     */
    val nextCursor: String? = null,
)

/** A decision about one string. */
@Serializable
data class ReviewDecisionRequest(
    val action: ReviewAction,
    /**
     * Required for [ReviewAction.REQUEST_CHANGES] and refused otherwise.
     *
     * An unexplained rejection is how review turns into resentment, so the note is a 400 rather than
     * a convention somebody follows on their good days.
     */
    val note: String? = null,
    /** The version being decided about, for the same reason the editor sends one. */
    val expectedUpdatedAt: String? = null,
)

/**
 * How many strings one batch decision may carry.
 *
 * A cap rather than a page size: the batch is the unit a reviewer confirms, and a confirm dialog
 * that says "3,412 strings" is one nobody reads before pressing. The client chunks anything larger
 * and shows a receipt per chunk.
 */
const val MAX_REVIEW_BATCH = 200

/** One string in a batch decision, with the version the reviewer was looking at. */
@Serializable
data class ReviewBatchItem(
    val keyId: String,
    /** The same guard the single-string route takes. Absent means unconditional. */
    val expectedUpdatedAt: String? = null,
)

/**
 * A decision about several strings, each of which is decided on its own merits.
 *
 * **Not a select-all grid.** The review lane's rule is that somebody read the string; what this
 * request carries is a set the reviewer has already seen on one screen, and every item still goes
 * through the same gate the single-string route applies — scope, role, `PENDING`, the expected
 * version, and a fresh server-side QA evaluation.
 */
@Serializable
data class ReviewBatchRequest(
    val items: List<ReviewBatchItem> = emptyList(),
    /** [ReviewAction.SKIP] is refused: skipping decides nothing, so a batch of it is a no-op. */
    val action: ReviewAction,
    /** Required for [ReviewAction.REQUEST_CHANGES], for the reason the single route requires it. */
    val note: String? = null,
    /**
     * Evaluate everything and write nothing.
     *
     * This is the preview the confirm dialog is built from: the same code path, the same gate, the
     * same per-item answers — so the count somebody agrees to is the count that happens, rather than
     * an estimate computed a second way in the browser.
     */
    val dryRun: Boolean = false,
)

/** What happened to one string in a batch. */
@Serializable
enum class ReviewBatchResult {
    /** Decided — or, under a dry run, would have been. */
    DECIDED,

    /** Somebody got there first: it was no longer `PENDING`. */
    ALREADY_REVIEWED,

    /** The text moved after the reviewer loaded it. Nothing was written. */
    STALE,

    /** The gate refused it — QA errors, or the string is no longer in this project's queue. */
    BLOCKED,
}

/**
 * One string's outcome.
 *
 * [checks] and [current] carry the same evidence the single-string route puts in `details`, so a
 * skipped item can be explained rather than merely counted.
 */
@Serializable
data class ReviewBatchOutcome(
    val keyId: String,
    val result: ReviewBatchResult,
    /** The QA checks that blocked approval. Empty unless [result] is [ReviewBatchResult.BLOCKED]. */
    val checks: List<QaCheck> = emptyList(),
    val message: String? = null,
    /** What the string says now, when [result] is [ReviewBatchResult.STALE]. */
    val current: TranslationDto? = null,
)

/**
 * The receipt.
 *
 * Partial-tolerant by construction: one blocked string does not refuse the other hundred, and every
 * item that was not decided says why.
 */
@Serializable
data class ReviewBatchResponse(
    val decided: Int = 0,
    val outcomes: List<ReviewBatchOutcome> = emptyList(),
    /** Echoed back so a client cannot mistake a preview for a receipt. */
    val dryRun: Boolean = false,
)
